import { useAuth } from "@clerk/clerk-react";
import QueryString from "qs";
import { useState } from "react";
import useSWR from "swr";
import { DeviceFilterType, TDevice } from "../types/Devices";
import { apiDelete, apiPatch, apiPost, fetcher } from "../utils/axios";
import { DefaultFilterType } from "../types/PageFilterTypes";
import {
  FileStateType,
  LocalFileType,
  ServerFileType,
} from "../components/photos";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";
import heic2any from "heic2any";

const ERROR_TOAST_STYLE = {
  className: "bg-red-600 text-zinc-50",
  progressClassName: "opacity-90 [background:#fafafa]",
};

const FILE_PROCESSING_OPTIONS = {
  maxSizeMB: 2,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  fileType: "image/jpeg",
  initialQuality: 0.85
}

const updatingPhotosNotification = () => toast("Updating photos on device..");

const updatingPhotosNotificationFailure = () =>
  toast(
    "Could not update photos for the device. An error occurred",
    ERROR_TOAST_STYLE
  );

const updatingPhotosNotificationSuccess = () =>
  toast("Photos updated on device successfully");

const savePhotosForDevice = async (
  photos: FileStateType[],
  deviceId: string | null,
  token: string | null
) => {
  let newFiles = photos.filter((p) => p.type === "local") as LocalFileType[];

  const removedFiles = (
    photos.filter((p) => p.type === "server") as ServerFileType[]
  ).filter((p) => p.isDeleted);

  if (newFiles.length > 0 || removedFiles.length > 0) {
    // don't show notification, it's overload.
    // updatingPhotosNotification();
  } else {
    return;
  }

  newFiles = await Promise.all(newFiles.map(async (f) => {
    try {
      let fileToCompress = f.file;
      
      // Convert HEIC to JPEG if needed
      if (f.file.type === "image/heic" || f.file.type === "image/heif" || f.file.name.toLowerCase().endsWith('.heic')) {
        console.log(`Converting HEIC file ${f.file.name} to JPEG...`);
        const convertedBlob = await heic2any({
          blob: f.file,
          toType: "image/jpeg",
          quality: 0.9
        });
        
        // heic2any might return an array of blobs for multi-page HEIC files
        const jpegBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
        
        // Create a new File object with the converted blob
        fileToCompress = new File([jpegBlob], f.file.name.replace(/\.(heic|HEIC)$/i, '.jpg'), {
          type: 'image/jpeg'
        });
        console.log(`Converted HEIC to JPEG: ${fileToCompress.size} bytes`);
      }
      
      const compressedFile = await imageCompression(fileToCompress, FILE_PROCESSING_OPTIONS)
      console.log(`Compressed ${f.file.name}: ${f.file.size} bytes -> ${compressedFile.size} bytes`)
      
      return {
        ...f,
        file: compressedFile
      }
    } catch (error) {
      console.error(`Failed to process ${f.file.name}:`, error)
      toast(`Failed to process image ${f.file.name}. Please try a different image.`, ERROR_TOAST_STYLE)
      throw error
    }
  }))

  for (let i = 0; i < newFiles.length; i++) {
    const formData = new FormData();

    formData.append("file", newFiles[i].file);

    try {
      await apiPost(`/devices/${deviceId}/images`, formData, token);
    } catch (err) {
      console.log(err);

      updatingPhotosNotificationFailure();

      return;
    }
  }

  for (let i = 0; i < removedFiles.length; i++) {
    const body = {
      fileUrl: removedFiles[i].url,
    };

    try {
      await apiDelete(`/devices/${deviceId}/images`, token, body);
    } catch (err) {
      console.log(err);

      updatingPhotosNotificationFailure();

      return;
    }
  }

  updatingPhotosNotificationSuccess();
};

const useDevices = (
  defaultFilter: DefaultFilterType & DeviceFilterType = {
    sort: "name|asc",
    limit: 20,
  }
) => {
  const { getToken } = useAuth();
  const [filter, setFilter] = useState(defaultFilter);

  const { data, error, mutate } = useSWR(
    ["/devices", `?${QueryString.stringify(filter)}`],
    async (args) => {
      const token = await getToken();
      const path = `${args[0]}${args[1] || ""}`;

      return fetcher(path, token);
    },
    {
      revalidateOnFocus: false,
    }
  );

  const updateFilter = (newFilter: DefaultFilterType & DeviceFilterType) => {
    setFilter((prev) => ({ ...prev, ...newFilter }));
  };

  const adoptDevice = async (deviceId: string) => {
    const token = await getToken();
    await apiPost(`/devices/${deviceId.toLowerCase()}/adopt`, {}, token);

    mutate();
  };

  const updateDeviceById = async (deviceId: string, data: Partial<TDevice>) => {
    const token = await getToken();
    console.log("data", data);
    await apiPatch(`/devices/${deviceId.toLowerCase()}`, data, token);

    mutate();
  };

  const deleteDevice = async (deviceId: string) => {
    const token = await getToken();
    await apiDelete(`/devices/${deviceId.toLowerCase()}`, token);
    mutate();
  };

  const savePhotos = async (
    photos: FileStateType[],
    deviceId: string | null
  ) => {
    const token = await getToken();

    await savePhotosForDevice(photos, deviceId, token);

    mutate();
  };

  return {
    devices: (data as TDevice[]) || [],
    isLoading: !error && !data,
    isError: error,
    adoptDevice,
    updateDeviceById,
    deleteDevice,
    mutate,
    filters: filter,
    updateFilter,
    savePhotos,
  };
};

const useDevice = (id: string | null) => {
  const { getToken } = useAuth();

  const { data, error, mutate } = useSWR(
    id ? `/devices/${id}` : null,
    async (url) => {
      const token = await getToken();
      return fetcher(url, token);
    }
  );

  const updateDevice = async (data: Partial<TDevice>) => {
    const token = await getToken();
    
    await apiPatch(`/devices/${id}`, data, token);

    // Temporarily disable auto-refresh to prevent hanging on old endpoints
    // mutate();
  };

  const savePhotos = async (photos: FileStateType[]) => {
    const token = await getToken();

    await savePhotosForDevice(photos, id, token);

    // Temporarily disable auto-refresh to prevent hanging on old endpoints
    // mutate();
  };

  const deleteDevice = async () => {
    if (!id) return;
    const token = await getToken();
    await apiDelete(`/devices/${id.toLowerCase()}`, token);
  };

  const updateWateringNeeds = async (wateringNeeds: number) => {
    if (!id) return;
    const token = await getToken();
    
    await apiPatch(`/devices/${id.toLowerCase()}/watering-needs`, { wateringNeeds }, token);
    mutate();
  };

  return {
    device: (data as TDevice) || {},
    isLoading: !error && !data,
    isError: error,
    updateDevice,
    savePhotos,
    deleteDevice,
    updateWateringNeeds,
  };
};

export { useDevice, useDevices };
